import { Input, Output } from '@tempo-io/sui-icons';
import { toast } from '@tempo-io/sui-sonner';
import { HTTPError } from 'ky';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SynchronizationStatusValues,
  SyncType,
  type WorkIntegrationResponse,
} from '@/common/api/types/workIntegrations';
import { usePermission } from '@/common/hooks/usePermission';
import { PushWarningDialog } from '@/features/settings/work-integrations/components/PushWarningDialog';
import { useScopedSync } from '@/features/settings/work-integrations/hooks/useScopedSync';
import { SyncSplitButton } from './SyncSplitButton';

const PRIMARY_SYNC_TYPE: SyncType = SyncType.PULL;

interface ItemSyncButtonProps {
  itemId: number;
  integration: WorkIntegrationResponse;
}

export const ItemSyncButton = ({ itemId, integration }: ItemSyncButtonProps) => {
  const { t } = useTranslation();
  const canSync = usePermission('sync_integrations');
  const [pushWarningOpen, setPushWarningOpen] = useState(false);
  const {
    sync: queueSync,
    isDisabled,
    integrationIsSyncing,
    inProgress,
  } = useScopedSync({
    workIntegrationId: integration.id,
    integrationStatus: integration.status,
    scope: { itemIds: [itemId] },
  });

  const syncIsDisabled = integration.synchronizationStatus !== SynchronizationStatusValues.ENABLED;
  const primaryIsPull = PRIMARY_SYNC_TYPE === SyncType.PULL;

  const sync = (syncType: SyncType) => {
    queueSync(syncType, {
      onSuccess: (result) => {
        if (result.type === 'no-items') {
          toast.info(t(($) => $.common.settings['work-integrations'].itemSync.nothingToSync));
          return;
        }
        if (result.type === 'no-field-mappings') {
          toast.warning(
            t(($) => $.common.settings['work-integrations'].toasts.sync.noFieldMappings, {
              integrationName: integration.name,
            }),
          );
          return;
        }
        toast.success(t(($) => $.common.settings['work-integrations'].itemSync.queued));
      },
      onError: (error) => {
        if (error instanceof HTTPError && error.response.status === 403) {
          toast.error(
            t(($) => $.common.settings['work-integrations'].toasts.sync.errorSyncDisabled),
          );
          return;
        }
        toast.error(
          t(($) => $.common.settings['work-integrations'].toasts.sync.error),
          {
            description: error.message,
          },
        );
      },
    });
  };

  const handleSelect = (syncType: SyncType) => {
    if (syncType === SyncType.PUSH && integration.hasMappingChangeSinceLastPull) {
      setPushWarningOpen(true);
      return;
    }
    sync(syncType);
  };

  if (!canSync) return null;

  return (
    <>
      <SyncSplitButton
        labels={{
          action: primaryIsPull
            ? t(($) => $.common.settings['work-integrations'].itemSync.pull)
            : t(($) => $.common.settings['work-integrations'].itemSync.push),
          options: t(($) => $.common.settings['work-integrations'].itemSync.options),
          pull: t(($) => $.common.settings['work-integrations'].itemSync.pull),
          push: t(($) => $.common.settings['work-integrations'].itemSync.push),
        }}
        primarySyncType={PRIMARY_SYNC_TYPE}
        onSelect={handleSelect}
        disabled={isDisabled || syncIsDisabled}
        inProgress={inProgress}
        unavailableReason={
          syncIsDisabled
            ? t(($) => $.common.settings['work-integrations'].itemSync.syncDisabled)
            : integrationIsSyncing
              ? t(($) => $.common.settings['work-integrations'].itemSync.syncRunning)
              : undefined
        }
        testIdPrefix="item-sync"
        actionIcon={primaryIsPull ? <Input /> : <Output />}
      />
      <PushWarningDialog
        open={pushWarningOpen}
        onOpenChange={setPushWarningOpen}
        onPull={() => sync(SyncType.PULL)}
        onPush={() => sync(SyncType.PUSH)}
      />
    </>
  );
};
